import dayjs from "dayjs";
import { destinationsApi } from "src/services/api";
import { isValidDateString } from "src/utils/date-utils";
import { buildSearchQuery } from "src/utils/query-utils";
import { getMainCarrierAirlineIcon } from "src/utils/results-utils";
import { parseIntFromString } from "src/utils/utils";
import { isLangAbsent, redirectWithLang } from "./utils/loader-utils";
import { nanoid } from "@reduxjs/toolkit";

export async function loader({ request, params }) {
  if (isLangAbsent(request)) return redirectWithLang(request);
  const url = new URL(request.url);
  const min = parseIntFromString(url.searchParams.get("min"));
  const max = parseIntFromString(url.searchParams.get("max"));
  const { type, origin } = params;
  const wildcardParams = params["*"]?.split("/");

  const [minStart, maxEnd] = [
    dayjs().startOf("day").unix(),
    dayjs().add(11, "M").endOf("month").startOf("day").unix(),
  ];

  let destination, start, end, sec_start, sec_end;
  if (wildcardParams.length > 2) {
    [destination, start, end] = wildcardParams;
    if (!isValidDateString(start)) {
      start = undefined;
    }
    if (!isValidDateString(end)) {
      end = undefined;
    }
  } else if (wildcardParams.length === 1 && isValidDateString(wildcardParams[0])) {
    start = wildcardParams[0];
  } else if (wildcardParams.length === 1) {
    destination = wildcardParams[0];
  } else if (wildcardParams.length === 2) {
    if (isValidDateString(wildcardParams[0])) {
      start = wildcardParams[0];
      if (isValidDateString(wildcardParams[1])) {
        end = wildcardParams[1];
      }
    } else {
      destination = wildcardParams[0];
      if (isValidDateString(wildcardParams[1])) {
        start = wildcardParams[1];
      }
    }
  }

  if (destination && destination.length === 3) {
    destination = destination.toUpperCase();
  } else {
    destination = undefined;
  }

  if (start) {
    sec_start = dayjs(start).startOf("day").unix();
    if (sec_start < minStart) {
      sec_start = minStart;
    }
  }

  if (end) {
    sec_end = dayjs(end).endOf("day").unix();
    if (sec_end > maxEnd) {
      sec_end = maxEnd;
    }
  }

  const promises = [];
  promises.push(
    destinationsApi
      .getCheapFlights(
        type === "rt" ? "roundtrip" : "oneway",
        origin,
        destination,
        min,
        max,
        start,
        end,
        !Boolean(destination)
      )
      .catch(console.error)
  );

  let results = [];
  let srcCityLocalised = origin;
  let destCityLocalised = destination;

  await Promise.allSettled(promises).then(([flights]) => {
    console.log(flights);
    if (flights.status === "fulfilled") {
      results = flights.value?.data || [];
    }
    if (results.length) {
      srcCityLocalised = results[0].src.city || origin;
      if (destination) {
        destCityLocalised = results[0].dest.city || destination;
      }
    }
  });

  results = results.map((flight) => ({
    ...flight,
    id: nanoid(),
    airlineIcon: getMainCarrierAirlineIcon({
      supplier: { code: flight.supplier },
      carrier: { code: flight.carrier },
    }),
    link:
      "/search/results?" +
      buildSearchQuery({
        tripType: type === "rt" ? "roundtrip" : "oneway",
        trips: [
          {
            srcLocation: {
              iata: flight.src.iata,
              city: flight.src.city,
              cityIata: flight.src.iata,
              isCity: true,
            },
            destLocation: {
              iata: flight.dest.iata,
              city: flight.dest.city,
              cityIata: flight.dest.iata,
              isCity: true,
            },
            startDate: dayjs.unix(flight.departure.timestamp).format("YYYY-MM-DD"),
            endDate:
              type === "rt" ? dayjs.unix(flight.return.timestamp).format("YYYY-MM-DD") : "",
          },
        ],
        passengers: { adult: 1, child: 0, infant: 0 },
      }),
  }));

  results.sort((a, b) => a.price - b.price);

  return {
    data: results,
    srcCityLocalised,
    destCityLocalised,
    destination,
    start: sec_start,
    end: sec_end,
    min,
    max,
    bestPrice: results[0]?.price,
  };
}
